import { useCallback } from 'react'

import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionArea, InspectionItemType } from '@editor/interfaces/inspection'
import { ShapeKeyType } from '@editor/interfaces/shape'
import { zeroPad } from '@editor/services/Util'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'

const usePlaneDetection = (): ElementsPanelConfig => {
  // Store
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)
  const checkedEditorElements = useSelector((state: RootState) => state.blueprint.drawings.document.editorElements)

  return {
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item'], area?: InspectionArea) => {
        // Surface plane is parent-level, ignore if parent is provided
        if (parent) return []

        const planeItems = inspectionItems.filter(
          (item) =>
            item.item_type === InspectionItemType.PolygonArea &&
            !item.volume_id &&
            (!area || item.inspection_area_id === area.inspection_area_id),
        )

        if (planeItems.length) {
          return [
            {
              key: 'panel-surface-plane',
              name: '面積',
              isVirtualContainer: true,
              isCountable: true,
              onSelect: false,
              onHover: true,
              isCheckable: true,
              aggregateSelection: true,
              children: [
                ...(planeItems
                  .map<EditorPanelItem | null>((item, index) => {
                    const poly = shapes.polygons.find((shape) => item.shape_ids.polygons.includes(shape.shape_id))
                    if (!poly) {
                      return null
                    }

                    return {
                      key: `panel-surface-plane-${item.inspection_item_id}`,
                      name: item?.part_name || `面積${zeroPad(index + 1, 3)}`,
                      shapeKey: ShapeKeyType.POLYGON,
                      item: poly,
                      inspectionItem: item,
                      order: poly.order_in_inspection_group,
                      isGrouped: !!poly.inspection_group_id,
                      isOrderable: false,
                      isCheckable: true,
                      isChecked: checkedEditorElements.polygons.some((shape) => shape.id === poly.shape_id),
                      onDelete: false,
                      onHover: true,
                    }
                  })
                  .filter(Boolean) as EditorPanelItem[]),
              ],
            },
          ]
        }

        return []
      },
      [inspectionItems, checkedEditorElements.polygons, shapes.polygons],
    ),
  }
}

export default usePlaneDetection
